// src/pages/pessoas/clientes/DashCliente.js
import React, { useEffect, useMemo, useState } from "react";
import "bootstrap/dist/css/bootstrap.min.css";
import {
  FaChartLine,
  FaExclamationTriangle,
  FaMoneyBillWave,
  FaPhoneAlt,
  FaShoppingBasket,
  FaSort,
  FaSortDown,
  FaSortUp,
  FaUserCheck,
  FaUserClock,
  FaUserPlus,
} from "react-icons/fa";
import { Bar, Pie } from "react-chartjs-2";
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  Tooltip,
  Legend,
  ArcElement,
} from "chart.js";

import "../../../components/leiaute/Content.css";
import InputCheck from "../../../components/input/InputCheck";
import { fetchData } from "../../../services/Api";
import { formatCurrency } from "../../../utils/numberUtils";

ChartJS.register(CategoryScale, LinearScale, BarElement, Title, Tooltip, Legend, ArcElement);

const chartColors = ["#0d6efd", "#198754", "#ffc107", "#dc3545", "#6f42c1", "#20c997", "#fd7e14", "#6c757d", "#0dcaf0", "#d63384"];

const formatDate = (date) => date.toISOString().split("T")[0];

const formatDateTime = (value) => {
  if (!value) return "-";
  return new Date(value).toLocaleDateString("pt-BR");
};

const formatPhone = (value) => value || "-";

const sortValue = (item, key) => {
  const value = item?.[key];
  if (value === null || value === undefined) return "";
  if (key.toLowerCase().includes("pedido") && typeof value === "string") {
    return new Date(value).getTime() || 0;
  }
  return value;
};

const sortItems = (items, sortConfig) => {
  return [...items].sort((a, b) => {
    const valueA = sortValue(a, sortConfig.key);
    const valueB = sortValue(b, sortConfig.key);

    if (typeof valueA === "string" || typeof valueB === "string") {
      return sortConfig.direction === "asc"
        ? String(valueA).localeCompare(String(valueB), "pt-BR")
        : String(valueB).localeCompare(String(valueA), "pt-BR");
    }

    return sortConfig.direction === "asc" ? valueA - valueB : valueB - valueA;
  });
};

const SortableHeader = ({ label, column, sortConfig, onSort, align = "start" }) => {
  const active = sortConfig.key === column;
  const Icon = !active ? FaSort : sortConfig.direction === "asc" ? FaSortUp : FaSortDown;

  return (
    <th className={align === "end" ? "text-end" : ""}>
      <button
        type="button"
        className={`btn btn-link btn-sm p-0 text-decoration-none ${align === "end" ? "text-end" : "text-start"}`}
        style={{ color: "#212529", fontWeight: 600, whiteSpace: "nowrap" }}
        onClick={() => onSort(column)}
      >
        {label} <Icon className="ms-1" />
      </button>
    </th>
  );
};

const MetricCard = ({ icon, title, value, color, background }) => (
  <div className="col-md">
    <div className="card text-center shadow-sm h-100" style={{ backgroundColor: background, border: "none" }}>
      <div className="card-body">
        {icon}
        <h6 className="card-title mt-2 mb-1" style={{ color }}>{title}</h6>
        <div className="fs-3 fw-bold text-dark">{value}</div>
      </div>
    </div>
  </div>
);

const RankingTable = ({ title, items, valueLabel, valueSelector }) => (
  <div className="card shadow-sm h-100">
    <div className="card-body">
      <h5 className="card-title">{title}</h5>
      <div className="table-responsive">
        <table className="table table-sm table-hover mb-0 align-middle">
          <thead>
            <tr>
              <th>Cliente</th>
              <th className="text-end">Pedidos</th>
              <th className="text-end">{valueLabel}</th>
            </tr>
          </thead>
          <tbody>
            {items.length === 0 && (
              <tr>
                <td colSpan="3" className="text-muted text-center">Nenhum cliente encontrado.</td>
              </tr>
            )}
            {items.map((item) => (
              <tr key={`${title}-${item.clienteId}`}>
                <td>
                  <div className="fw-semibold">{item.nomeCliente}</div>
                  <small className="text-muted"><FaPhoneAlt className="me-1" />{formatPhone(item.telefone)}</small>
                </td>
                <td className="text-end">{item.qtdePedidos}</td>
                <td className="text-end">{valueSelector(item)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  </div>
);

function DashCliente() {
  const today = new Date();
  const firstDayOfMonth = new Date(today.getFullYear(), today.getMonth(), 1);
  const [startDate, setStartDate] = useState(formatDate(firstDayOfMonth));
  const [endDate, setEndDate] = useState(formatDate(today));
  const [excluirClientePadrao, setExcluirClientePadrao] = useState(true);
  const [loading, setLoading] = useState(false);
  const [inativosSort, setInativosSort] = useState({ key: "diasSemPedido", direction: "desc" });
  const [recorrenciaSort, setRecorrenciaSort] = useState({ key: "qtdePedidos", direction: "desc" });
  const [dashClientes, setDashClientes] = useState({
    cards: {},
    clientesMaisPedidos: [],
    clientesMaiorValor: [],
    clientesMaiorTicketMedio: [],
    clientesInativos: [],
    clientesRecorrencia: [],
  });

  useEffect(() => {
    const carregarDashCliente = async () => {
      setLoading(true);
      try {
        const data = await fetchData("/DashCliente", {
          DataIni: startDate,
          DataFim: `${endDate} 23:59:59`,
          ExcluirClientePadrao: excluirClientePadrao,
        });

        setDashClientes({
          cards: data.cards || {},
          clientesMaisPedidos: data.clientesMaisPedidos || [],
          clientesMaiorValor: data.clientesMaiorValor || [],
          clientesMaiorTicketMedio: data.clientesMaiorTicketMedio || [],
          clientesInativos: data.clientesInativos || [],
          clientesRecorrencia: data.clientesRecorrencia || [],
        });
      } catch (error) {
        console.error("Erro ao carregar o dashboard de clientes:", error);
      } finally {
        setLoading(false);
      }
    };

    carregarDashCliente();
  }, [startDate, endDate, excluirClientePadrao]);

  const cards = dashClientes.cards || {};
  const clientesMaisPedidos = dashClientes.clientesMaisPedidos || [];
  const clientesMaiorValor = dashClientes.clientesMaiorValor || [];
  const clientesMaiorTicketMedio = dashClientes.clientesMaiorTicketMedio || [];
  const clientesInativos = dashClientes.clientesInativos || [];
  const clientesRecorrencia = dashClientes.clientesRecorrencia || [];

  const handleSort = (setSortConfig) => (key) => {
    setSortConfig((current) => ({
      key,
      direction: current.key === key && current.direction === "desc" ? "asc" : "desc",
    }));
  };

  const clientesInativosOrdenados = useMemo(() => {
    return sortItems(clientesInativos, inativosSort);
  }, [clientesInativos, inativosSort]);

  const clientesRecorrenciaOrdenados = useMemo(() => {
    return sortItems(clientesRecorrencia, recorrenciaSort);
  }, [clientesRecorrencia, recorrenciaSort]);

  const barMaisPedidos = useMemo(() => ({
    labels: clientesMaisPedidos.map((item) => item.nomeCliente),
    datasets: [
      {
        label: "Pedidos",
        data: clientesMaisPedidos.map((item) => item.qtdePedidos),
        backgroundColor: "#0d6efd",
      },
    ],
  }), [clientesMaisPedidos]);

  const pieInatividade = useMemo(() => {
    const agrupado = clientesInativos.reduce((acc, item) => {
      acc[item.faixaInatividade] = (acc[item.faixaInatividade] || 0) + 1;
      return acc;
    }, {});

    return {
      labels: Object.keys(agrupado),
      datasets: [
        {
          data: Object.values(agrupado),
          backgroundColor: chartColors,
        },
      ],
    };
  }, [clientesInativos]);

  const tendenciaBadge = (tendencia) => {
    if (tendencia === "Aumentando") return "bg-success";
    if (tendencia === "Caindo") return "bg-danger";
    return "bg-secondary";
  };

  return (
    <div className="p-4 dashboard-scroll">
      <div className="d-flex justify-content-between align-items-start flex-wrap gap-3 mb-3" style={{ marginTop: "-20px" }}>
        <div>
          <h2 className="mb-1">Dashboard de Clientes</h2>
          <small className="text-muted">Comportamento, recorrência e clientes que precisam de atenção.</small>
        </div>

        <div className="d-flex align-items-end flex-wrap gap-2">
          <div style={{ maxWidth: "130px" }}>
            <label htmlFor="dashClientesStartDate" className="form-label">Data Inicial:</label>
            <input
              type="date"
              id="dashClientesStartDate"
              className="form-control"
              value={startDate}
              onChange={(e) => setStartDate(e.target.value)}
            />
          </div>
          <div style={{ maxWidth: "130px" }}>
            <label htmlFor="dashClientesEndDate" className="form-label">Data Final:</label>
            <input
              type="date"
              id="dashClientesEndDate"
              className="form-control"
              value={endDate}
              onChange={(e) => setEndDate(e.target.value)}
            />
          </div>
          <div className="pb-2">
            <InputCheck
              id="dashClientesExcluirClientePadrao"
              label="Excluir cliente padrão"
              checked={excluirClientePadrao}
              onChange={(e) => setExcluirClientePadrao(e.target.checked)}
            />
          </div>
        </div>
      </div>

      {loading && <div className="alert alert-info py-2">Carregando dashboard de clientes...</div>}

      <div className="row g-3">
        <MetricCard
          icon={<FaUserCheck size={34} color="#0d6efd" />}
          title="Clientes Ativos"
          value={cards.clientesAtivos || 0}
          color="#0d6efd"
          background="#eef5ff"
        />
        <MetricCard
          icon={<FaUserPlus size={34} color="#198754" />}
          title="Clientes Novos"
          value={cards.clientesNovos || 0}
          color="#198754"
          background="#e2f0d9"
        />
        <MetricCard
          icon={<FaUserClock size={34} color="#fd7e14" />}
          title="Clientes Inativos"
          value={cards.clientesInativos || 0}
          color="#fd7e14"
          background="#fff7e6"
        />
        <MetricCard
          icon={<FaExclamationTriangle size={34} color="#dc3545" />}
          title="Clientes em Risco"
          value={cards.clientesEmRisco || 0}
          color="#dc3545"
          background="#ffecec"
        />
        <MetricCard
          icon={<FaMoneyBillWave size={34} color="#6f42c1" />}
          title="Venda Recorrente"
          value={formatCurrency(cards.valorClientesRecorrentes || 0)}
          color="#6f42c1"
          background="#f1ecff"
        />
      </div>

      <div className="row g-3 mt-3">
        <div className="col-md-6">
          <div className="card shadow-sm h-100">
            <div className="card-body">
              <div className="d-flex align-items-center justify-content-between">
                <h5 className="card-title mb-0">Clientes que Mais Pediram</h5>
                <FaShoppingBasket title="Clientes que mais fizeram pedidos" />
              </div>
              <div className="mt-3">
                <Bar data={barMaisPedidos} />
              </div>
            </div>
          </div>
        </div>
        <div className="col-md-3">
          <RankingTable
            title="Maior Valor"
            items={clientesMaiorValor}
            valueLabel="Valor"
            valueSelector={(item) => formatCurrency(item.valorTotal || 0)}
          />
        </div>
        <div className="col-md-3">
          <RankingTable
            title="Maior Ticket Médio"
            items={clientesMaiorTicketMedio}
            valueLabel="Ticket"
            valueSelector={(item) => formatCurrency(item.ticketMedio || 0)}
          />
        </div>
      </div>

      <div className="row g-3 mt-3">
        <div className="col-md-7">
          <div className="card shadow-sm h-100">
            <div className="card-body py-2">
              <h5 className="card-title">Clientes sem Pedido</h5>
              <div className="table-responsive" style={{ maxHeight: "640px", overflowY: "auto", fontSize: "0.875rem" }}>
                <table className="table table-sm table-hover mb-0 align-middle">
                  <thead className="table-light" style={{ position: "sticky", top: 0, zIndex: 1 }}>
                    <tr>
                      <SortableHeader label="Cliente" column="nomeCliente" sortConfig={inativosSort} onSort={handleSort(setInativosSort)} />
                      <SortableHeader label="Faixa" column="faixaInatividade" sortConfig={inativosSort} onSort={handleSort(setInativosSort)} />
                      <SortableHeader label="Último Pedido" column="ultimoPedido" sortConfig={inativosSort} onSort={handleSort(setInativosSort)} />
                      <SortableHeader label="Histórico" column="valorTotalHistorico" sortConfig={inativosSort} onSort={handleSort(setInativosSort)} align="end" />
                    </tr>
                  </thead>
                  <tbody>
                    {clientesInativosOrdenados.length === 0 && (
                      <tr>
                        <td colSpan="4" className="text-muted text-center">Nenhum cliente inativo encontrado.</td>
                      </tr>
                    )}
                    {clientesInativosOrdenados.map((item) => (
                      <tr key={`inativo-${item.clienteId}`}>
                        <td>
                          <div className="fw-semibold">{item.nomeCliente}</div>
                          <small className="text-muted"><FaPhoneAlt className="me-1" />{formatPhone(item.telefone)}</small>
                        </td>
                        <td>{item.faixaInatividade}</td>
                        <td>{formatDateTime(item.ultimoPedido)}</td>
                        <td className="text-end">{formatCurrency(item.valorTotalHistorico || 0)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        </div>
        <div className="col-md-5">
          <div className="card shadow-sm h-100">
            <div className="card-body">
              <h5 className="card-title">Inatividade por Faixa</h5>
              <Pie data={pieInatividade} />
            </div>
          </div>
        </div>
      </div>

      <div className="card shadow-sm mt-4">
        <div className="card-body py-2">
          <div className="d-flex align-items-center justify-content-between">
            <h5 className="card-title mb-0">Recorrência</h5>
            <FaChartLine title="Frequência e tendência dos clientes" />
          </div>
          <div className="table-responsive mt-2" style={{ maxHeight: "600px", overflowY: "auto", fontSize: "0.875rem" }}>
            <table className="table table-sm table-hover mb-0 align-middle">
              <thead className="table-light" style={{ position: "sticky", top: 0, zIndex: 1 }}>
                <tr>
                  <SortableHeader label="Cliente" column="nomeCliente" sortConfig={recorrenciaSort} onSort={handleSort(setRecorrenciaSort)} />
                  <SortableHeader label="Pedidos" column="qtdePedidos" sortConfig={recorrenciaSort} onSort={handleSort(setRecorrenciaSort)} align="end" />
                  <SortableHeader label="Valor" column="valorTotal" sortConfig={recorrenciaSort} onSort={handleSort(setRecorrenciaSort)} align="end" />
                  <SortableHeader label="Frequência Média" column="frequenciaMediaDias" sortConfig={recorrenciaSort} onSort={handleSort(setRecorrenciaSort)} align="end" />
                  <SortableHeader label="Dias sem Pedido" column="diasDesdeUltimoPedido" sortConfig={recorrenciaSort} onSort={handleSort(setRecorrenciaSort)} align="end" />
                  <SortableHeader label="Tendência" column="tendencia" sortConfig={recorrenciaSort} onSort={handleSort(setRecorrenciaSort)} />
                </tr>
              </thead>
              <tbody>
                {clientesRecorrenciaOrdenados.length === 0 && (
                  <tr>
                    <td colSpan="6" className="text-muted text-center">Nenhuma recorrência encontrada.</td>
                  </tr>
                )}
                {clientesRecorrenciaOrdenados.map((item) => (
                  <tr key={`recorrencia-${item.clienteId}`}>
                    <td>
                      <div className="fw-semibold">{item.nomeCliente}</div>
                      <small className="text-muted"><FaPhoneAlt className="me-1" />{formatPhone(item.telefone)}</small>
                    </td>
                    <td className="text-end">{item.qtdePedidos}</td>
                    <td className="text-end">{formatCurrency(item.valorTotal || 0)}</td>
                    <td className="text-end">{Number(item.frequenciaMediaDias || 0).toFixed(1)} dias</td>
                    <td className="text-end">{item.diasDesdeUltimoPedido}</td>
                    <td><span className={`badge ${tendenciaBadge(item.tendencia)}`}>{item.tendencia}</span></td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </div>
  );
}

export default DashCliente;
